import { useCallback, useLayoutEffect, useRef, useState, type WheelEvent } from 'react';

export interface HorizontalScrollMetrics {
  clientWidth: number;
  scrollLeft: number;
  scrollWidth: number;
}

export const emptyHorizontalScrollMetrics: HorizontalScrollMetrics = { clientWidth: 0, scrollLeft: 0, scrollWidth: 0 };

/** 横向滚动容器的基础度量；共享 Table 会在它之上再补固定列宽度。 */
export function readHorizontalScrollMetrics(element: HTMLElement): HorizontalScrollMetrics {
  return { clientWidth: element.clientWidth, scrollLeft: element.scrollLeft, scrollWidth: element.scrollWidth };
}

/**
 * 横向滚动容器：一条 ref、一份度量、四个交给容器的处理器。
 *
 * 共享 Table 与订单预览抽屉都要「自绘一条横条」——原生滚动条在 macOS 上是 overlay，滚完就隐、
 * 不占布局，用户看不出右边还有内容；而两边的滚动容器互不相同（组件内的 `.scroll` / 抽屉里的表格视口），
 * 所以度量口径、resize 监听、Shift + 滚轮与拖动后的回写都收在这里，避免出现「一个用原生条、
 * 一个用自绘条」的两套观感。真正的横条由 `HorizontalScrollbar` 画，两处 import 同一份组件与样式。
 *
 * `readMetrics` 由调用方给：Table 要顺带读固定列宽度（算固定区阴影），预览只要基础三项。
 */
export function useHorizontalScroll<Metrics extends HorizontalScrollMetrics>(
  readMetrics: (element: HTMLDivElement) => Metrics,
  initialMetrics: Metrics,
  // 列/数据变化后要立刻重算；依赖长度由调用方保证固定，所以按数组整体传入。
  deps: readonly unknown[] = [],
) {
  const nodeRef = useRef<HTMLDivElement | null>(null);
  const [node, setNode] = useState<HTMLDivElement | null>(null);
  const readRef = useRef(readMetrics);
  /*
   * 同步最新的读取口径。写在 effect 里而不是渲染期间——渲染期写 ref 会被 `react-hooks/refs` 拦下
   * （渲染不该有副作用，并发下还可能写进被丢弃的那次渲染）。这个 effect 声明在下面的测量 effect 之前，
   * 同一次提交里必然先更新口径再测量。
   */
  useLayoutEffect(() => {
    readRef.current = readMetrics;
  }, [readMetrics]);
  const [metrics, setMetrics] = useState<Metrics>(initialMetrics);
  const maxScrollLeft = Math.max(0, metrics.scrollWidth - metrics.clientWidth);

  /*
   * 回调 ref，不是对象 ref：订单预览抽屉是「打开时才把表格挂进 DOM」的。对象 ref 在首帧是空的，
   * 而 effect 的依赖（列数、页码）之后一直不变，容器又还不存在、没有 resize / observer 兜底，
   * 于是量到的永远是 0、横条根本不渲染（实测：抽屉打开后 DOM 里没有 `[role="scrollbar"]`）。
   * 回调 ref 让「节点挂上」本身成为一次依赖变化，挂上就测、拆下就停。
   */
  const ref = useCallback((next: HTMLDivElement | null) => {
    nodeRef.current = next;
    setNode(next);
  }, []);

  useLayoutEffect(() => {
    if (!node) return undefined;
    const sync = () => setMetrics(readRef.current(node));
    sync();
    window.addEventListener('resize', sync);
    const observer = typeof ResizeObserver === 'undefined' ? undefined : new ResizeObserver(sync);
    observer?.observe(node);
    /*
     * 连内容一起看着：容器自己的盒子常常不变（宽度由父级给、高度由 flex 定），
     * 换列 / 加行只会改内容宽度，那时候上面那次 observe 不响，横条长度就对不上了。
     */
    if (node.firstElementChild) observer?.observe(node.firstElementChild);
    return () => {
      window.removeEventListener('resize', sync);
      observer?.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- 依赖由调用方按「什么时候该重算」给出，外加节点挂载时机
  }, [node, ...deps]);

  function setScrollLeft(next: number) {
    const element = nodeRef.current;
    if (!element) return;
    element.scrollLeft = Math.max(0, Math.min(maxScrollLeft, next));
    setMetrics(readRef.current(element));
  }

  function onScroll() {
    const element = nodeRef.current;
    if (element) setMetrics(readRef.current(element));
  }

  /** Shift + 滚轮转横向：滚动条本身在容器外面，滚轮落在表体上也要能横着走。 */
  function onWheel(event: WheelEvent<HTMLDivElement>) {
    if (!event.shiftKey || Math.abs(event.deltaY) <= Math.abs(event.deltaX)) return;
    event.preventDefault();
    setScrollLeft(metrics.scrollLeft + event.deltaY);
  }

  return { ref, metrics, maxScrollLeft, setScrollLeft, onScroll, onWheel };
}
